import { Logotype } from "../components/PixelArt";
import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import { playAcknowledgement } from "../audio";
import { isStandalone } from "../components/InstallHint";
import { StoreBadges } from "../components/StoreBadges";
import { PASSWORD_COPY, hasPasswordLine } from "../../shared/passwords.ts";
import { heldNameTyped } from "../passwords-ui.ts";
import { usePasskeysUI } from "../features";
import type { AuthState } from "../../shared/types";
import { NAME_MAX, normalizeName } from "../../shared/peons.ts";
import { BusyLabel } from "../components/BusyLabel";

/**
 * Who are you? A name typed here is this device's name. A name someone holds (their person has a
 * password or a passkey, docs/members.md "Personal passwords") opens a password field under it and
 * signs in as them instead: `held` is what the app already knows is held (the name a 403 name_held
 * named), `loadHeld` asks the server for the rest once this device is signed into a household.
 */
export function NameGate({
  onSubmit,
  initialName,
  held: knownHeld,
  loadHeld,
  onSignedIn,
}: {
  onSubmit: (name: string) => void;
  initialName?: string;
  held?: string[];
  loadHeld?: () => Promise<string[]>;
  onSignedIn?: (state: AuthState) => void;
}) {
  const [name, setName] = useState(initialName ?? "");
  const [held, setHeld] = useState<string[]>(knownHeld ?? []);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!loadHeld) return;
    let live = true;
    loadHeld().then(
      (list) => live && setHeld((h) => [...new Set([...h, ...list])]),
      () => {},
    );
    return () => {
      live = false;
    };
    // Once: or when the app learns this device is signed in (the gate mounts before it knows).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!loadHeld]);
  const trimmed = name.trim();
  const heldName = onSignedIn ? heldNameTyped(trimmed, held) : null;
  // The line about names with passwords means nothing to a brand-new visitor: it shows once the
  // typed name is held, or where the household has passwords switched on (the `passkeys` flag).
  const passwordsOn = usePasskeysUI();
  // A new visitor meets this gate first, so it carries the same store badges as the login gate.
  const badges = !isStandalone();

  async function signIn(heldAs: string) {
    if (busy || !password) return;
    setBusy(true);
    setError(null);
    try {
      onSignedIn?.(await api.auth.person(heldAs, password));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not sign in");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main class={badges ? "gate gate-with-footer" : "gate"}>
      <img class="gate-logo" src="/ui/peon-original@3x.png" alt="" />
      <Logotype />
      <p class="muted">Something need doing? First, who are you?</p>
      <form
        class="gate-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!trimmed) return;
          void playAcknowledgement();
          if (heldName) void signIn(heldName);
          // Stored as the server compares it: inner double spaces would make a different created_by.
          else onSubmit(normalizeName(name));
        }}
      >
        <input
          class="input"
          type="text"
          autocomplete="given-name"
          autoFocus={!heldName}
          maxLength={NAME_MAX}
          placeholder="Your name"
          aria-label="Your name"
          value={name}
          onInput={(e) => {
            setName(e.currentTarget.value);
            setError(null);
          }}
        />
        {heldName && (
          <>
            <p class="muted small name-held-line">{hasPasswordLine(heldName)}</p>
            <input
              class="input"
              type="password"
              autocomplete="current-password"
              autoFocus
              placeholder={PASSWORD_COPY.passwordField}
              aria-label={PASSWORD_COPY.passwordField}
              value={password}
              onInput={(e) => setPassword(e.currentTarget.value)}
            />
          </>
        )}
        <button class="btn btn-primary" type="submit" disabled={busy || !trimmed || (!!heldName && !password)}>
          <BusyLabel busy={busy} label="Checking…">{heldName ? PASSWORD_COPY.enter : "Ready to work"}</BusyLabel>
        </button>
        {error && <div class="form-error" role="alert">{error}</div>}
      </form>
      {(heldName || passwordsOn) && <p class="muted small">{PASSWORD_COPY.nameGateLine}</p>}
      {badges && (
        <footer class="gate-footer">
          <StoreBadges />
        </footer>
      )}
    </main>
  );
}
